import { formatDate, formatMoney, formatMovement, t } from "@hakili/i18n";
import { Button, Card, Field, METADONNEE, Modal, Money, TEXTE_SECONDAIRE } from "@hakili/ui";
import { ChevronDown, TrendingDown, TrendingUp } from "lucide-react";
import { useCallback, useEffect, useState } from "react";

import type { DealerBalance, DoorResult, Inventory, PendingTopup } from "../session/useSession.js";
import type { DealerSettlement } from "../session/settlements.js";
import { ReadFailure } from "./read-failure.js";
import { Sales } from "./argent-ventes.js";

/** `P1-DLW-054` (designer R's review of #920): the kit's metadata colour (7,5:1) at 13 px, never #9CA3AF at 12 px (2,54:1). */
const META_STYLE = { color: "var(--hk-text-2, #46536f)" } as const;
const META_SIZE = "text-[13px]";

/**
 * `round T-1d`, `M4`: **« Solde prépayé », the ledger's sum** , as
 * `hakili-dealer.jsx`'s `SoldeScreen` draws it, read from the dealer's own
 * door (`GET /api/v1/me/balance`, WF-DLR-13, 02 §2.3.8, I18).
 *
 * ## Every figure is the door's
 *
 * `round F5` printed no balance, because no door carried one and a `0 FCFA`
 * rendered from nothing is a number a dealership acts on. The door now stands,
 * and the sum it answers is `payments.v_dealer_balances`' , the sum of the
 * movements, never a column , so a zero here is a zero READ, and it is shown.
 * What still has no door keeps `R81`'s rule: the advances to come are
 * `P2-PAY-009`'s, booked with the mise à disposition, and the line says so in
 * words rather than printing a zero.
 *
 * ## The commission notice, its figures now the dealer's own
 *
 * `F5` checked the notice against 02 §2.4.13 line by line and copied it with
 * « 10 % » and « 90 % » as literals. `T-1c` amended I31: the rate is in two
 * layers, the dealer's convention term where one stands and the published
 * standard otherwise. The door answers that rate and the notice substitutes
 * it, so a dealership at 8 % reads « 92 % sur votre compte et 8 % ».
 *
 * ## « Recharger mon solde » declares, and credits nothing
 *
 * WF-DLR-13 step 3 read « Il crédite immédiatement »; the addendum of this
 * round amends it, both texts: no provider stands (04 A3 open), so the dealer
 * transfers to Hakili's own account , published by the founder's settings door
 * and read here, never typed , and DECLARES the transfer with his reference.
 * The credit movement is born when Hakili confirms on evidence (row 24). The
 * modal says so, the pending list says « en attente de confirmation par
 * Hakili », and the sum never carries a declared amount.
 */

/** The drawing's own presets (`MONTANTS_RECHARGE`), in francs. */
const PRESETS: readonly number[] = [50_000, 100_000, 200_000, 500_000];

export interface BalanceProps {
  readonly readBalance: () => Promise<DoorResult<DealerBalance>>;
  readonly declareTopup: (input: {
    amount: number;
    transferReference: string;
  }) => Promise<DoorResult<PendingTopup>>;
  /**
   * `DEALER V2 B-D1`, ARGENT: the sales in progress and the two gestures on them, by the doors the car page already calls. Optional: a screen given none draws no sales (the balance alone, as before).
   */
  readonly readSettlements?: () => Promise<DoorResult<readonly DealerSettlement[]>>;
  readonly confirmReceipt?: (settlementId: string, receivedOn: string) => Promise<DoorResult<unknown>>;
  readonly reportNonReceipt?: (settlementId: string) => Promise<DoorResult<unknown>>;
  /** The inventory door, for the SERVER's instant (`asOf`, A50): the day the confirmation of receipt defaults to and never exceeds. */
  readonly listVehicles?: () => Promise<DoorResult<Inventory>>;
}

type Phase = "chargement" | "lecture";

const asDate = (iso: string): Date => new Date(iso);

export function Balance({
  readBalance,
  declareTopup,
  readSettlements,
  confirmReceipt,
  reportNonReceipt,
  listVehicles,
}: BalanceProps): React.JSX.Element {
  const [phase, setPhase] = useState<Phase>("chargement");
  const [ledger, setLedger] = useState<DealerBalance | null>(null);
  const [refus, setRefus] = useState<string | null>(null);
  const [ouvert, setOuvert] = useState(false);
  const [amount, setAmount] = useState<number>(PRESETS[2] ?? 0);
  const [customAmount, setCustomAmount] = useState("");
  const [reference, setReference] = useState("");
  const [erreur, setErreur] = useState<string | null>(null);
  const [enCours, setEnCours] = useState(false);
  const [declaree, setDeclaree] = useState<number | null>(null);

  const charger = useCallback(async (): Promise<void> => {
    const answer = await readBalance();
    if (answer.ok) {
      setLedger(answer.value);
      setRefus(null);
    } else {
      setRefus(answer.message);
    }
    setPhase("lecture");
  }, [readBalance]);

  useEffect(() => {
    void charger();
  }, [charger]);

  /** `DEALER V2 B-D1`: the sales in progress. A refused read is SAID with its retry, never drawn as « no sale » (A47, A5). */
  const [sales, setSales] = useState<readonly DealerSettlement[] | null>(null);
  const [salesRefusal, setSalesRefusal] = useState<string | null>(null);
  const loadSales = useCallback(async (): Promise<void> => {
    if (readSettlements === undefined) return;
    const answer = await readSettlements();
    if (answer.ok) {
      setSales(answer.value);
      setSalesRefusal(null);
    } else {
      setSalesRefusal(answer.message);
    }
  }, [readSettlements]);
  useEffect(() => {
    void loadSales();
  }, [loadSales]);
  /** The server's day, read when the dialog opens: `asOf` of the inventory door, `null` when it refuses or is not given. */
  const readToday = useCallback(async (): Promise<string | null> => {
    if (listVehicles === undefined) return null;
    const answer = await listVehicles();
    return answer.ok ? answer.value.asOf.slice(0, 10) : null;
  }, [listVehicles]);

  const ouvrir = (): void => {
    setErreur(null);
    setDeclaree(null);
    setReference("");
    setCustomAmount("");
    setAmount(PRESETS[2] ?? 0);
    setOuvert(true);
  };

  /** The amount the declaration carries: a preset, the shortfall, or the typed figure. */
  const chosenAmount = customAmount.trim() === "" ? amount : Number.parseInt(customAmount, 10);
  const amountValid = Number.isInteger(chosenAmount) && chosenAmount > 0;

  const declarer = async (): Promise<void> => {
    if (enCours) return;
    setEnCours(true);
    try {
      const answer = await declareTopup({
        amount: amountValid ? chosenAmount : 0,
        transferReference: reference.trim(),
      });
      if (!answer.ok) {
        // The door's own sentence; the form stays open holding what was typed.
        setErreur(answer.message);
        return;
      }
      setErreur(null);
      setOuvert(false);
      setDeclaree(answer.value.amount);
      // Re-read rather than sum locally: the pending list is the door's too.
      await charger();
    } finally {
      setEnCours(false);
    }
  };

  const rate = ledger?.commission.ratePct ?? null;
  // The shortfall preset: only when the advances' total is a figure the door
  // gave, and only when the balance does not cover it.
  const manque =
    ledger !== null && ledger.upcomingAdvancesTotal !== null && ledger.upcomingAdvancesTotal > ledger.balance
      ? ledger.upcomingAdvancesTotal - ledger.balance
      : null;

  return (
    <div className="flex flex-col gap-6" data-testid="solde">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight" data-testid="solde-titre">
            {t("dealer.argent.titre")}
          </h1>
          <p className={`mt-1.5 text-sm ${TEXTE_SECONDAIRE}`}>{t("dealer.argent.resume")}</p>
        </div>
        {/* The frame's header button, a SECONDARY: the page's one primary is « Confirmer la réception » on the sale that waits for the dealer. Never offered on a balance nobody read. */}
        {phase === "lecture" && ledger !== null ? (
          <Button variant="secondary" onClick={ouvrir} data-testid="solde-recharger">
            {t("dealer.solde.recharger")}
          </Button>
        ) : null}
      </div>

      {phase === "chargement" ? (
        <Card>
          <p className={`text-sm ${TEXTE_SECONDAIRE}`} data-testid="solde-chargement">
            {t("common.state.chargement")}
          </p>
        </Card>
      ) : ledger === null ? (
        <ReadFailure
          what="dealer.lecture.chiffres"
          message={refus ?? t("common.error.connexionEchouee")}
          onRetry={() => {
            void charger();
          }}
          testId="solde-refus"
          retryTestId="solde-reessayer"
        />
      ) : (
        <>
          <Card>
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className={`${METADONNEE} ${TEXTE_SECONDAIRE}`}>{t("dealer.solde.disponible")}</p>
                <p className="mt-1 text-3xl font-semibold tabular-nums" data-testid="solde-disponible">
                  <Money amount={ledger.balance} />
                </p>
                {/* `T-2c`, `M2 (5)` (WF-CON-38 signal 7, `P2-PAY-010`): the notice when the balance stands
                    under three times the control's price , the door's own fact, the same crossing the
                    signal was raised at; the top-up path beside it. */}
                {ledger.isLow && ledger.lowThreshold !== null ? (
                  <p
                    className="mt-2 rounded-lg bg-amber-50 px-2 py-1 text-xs text-amber-700"
                    data-testid="solde-bas"
                  >
                    {t("dealer.solde.bas").replace("{seuil}", formatMoney(ledger.lowThreshold))}
                  </p>
                ) : null}
                {/* `R81`: the advances to come are `P2-PAY-009`'s figure; until
                    the mise à disposition stands, a sentence and never a zero. */}
                {ledger.upcomingAdvancesTotal === null ? (
                  <p className={`mt-1 ${META_SIZE}`} style={META_STYLE} data-testid="solde-avances-a-venir">
                    {t("dealer.solde.avancesAVenirAbsentes")}
                  </p>
                ) : (
                  <p
                    className={`mt-1 ${META_SIZE} ${manque === null ? "" : "text-rose-600"}`}
                    {...(manque === null ? { style: META_STYLE } : {})}
                    data-testid="solde-avances-a-venir"
                  >
                    {t("dealer.solde.avancesAVenir").replace(
                      "{montant}",
                      formatMoney(ledger.upcomingAdvancesTotal),
                    )}
                  </p>
                )}
                {ledger.lastMovementOn === null ? null : (
                  <p className={`mt-1 ${META_SIZE}`} style={META_STYLE} data-testid="solde-dernier-mouvement">
                    {t("dealer.solde.dernierMouvement").replace(
                      "{date}",
                      formatDate(asDate(ledger.lastMovementOn)),
                    )}
                  </p>
                )}
              </div>
            </div>
            {declaree === null ? null : (
              <p
                className="mt-4 border-t border-gray-100 pt-3 text-sm text-emerald-700"
                data-testid="solde-recharge-declaree"
              >
                {t("dealer.solde.rechargeDeclaree").replace("{montant}", formatMoney(declaree))}
              </p>
            )}
          </Card>

          {/* `DEALER V2 B-D1`, ARGENT: one card per sale in progress, from the settlements door; a refused read is said, with its retry. */}
          {salesRefusal === null ? null : (
            <ReadFailure
              what="dealer.lecture.chiffres"
              message={salesRefusal}
              onRetry={() => {
                void loadSales();
              }}
              testId="argent-refus"
              retryTestId="argent-ventes-reessayer"
            />
          )}
          {sales === null || confirmReceipt === undefined || reportNonReceipt === undefined ? null : (
            <Sales
              settlements={sales}
              confirmReceipt={confirmReceipt}
              reportNonReceipt={reportNonReceipt}
              readToday={readToday}
              onChanged={() => {
                void loadSales();
              }}
            />
          )}

          {ledger.pendingTopups.length === 0 ? null : (
            <>
              <h2 className="text-sm font-semibold">{t("dealer.solde.enAttenteTitre")}</h2>
              <Card>
                <ul className="divide-y divide-gray-100" data-testid="solde-recharges-en-attente">
                  {ledger.pendingTopups.map((topup) => (
                    <li key={topup.id} className="flex items-center gap-3 py-3 first:pt-0 last:pb-0">
                      <div className="flex-1">
                        <p className="text-sm text-gray-700">
                          {t("dealer.solde.enAttenteLigne")
                            .replace("{montant}", formatMoney(topup.amount))
                            .replace("{reference}", topup.transferReference)}
                        </p>
                        <p className={META_SIZE} style={META_STYLE}>
                          {t("dealer.solde.enAttenteDeclaree").replace(
                            "{date}",
                            formatDate(asDate(topup.declaredAt)),
                          )}
                        </p>
                      </div>
                      <span className="rounded-full bg-amber-50 px-2 py-0.5 text-xs font-medium text-amber-700">
                        {t("dealer.solde.enAttenteEtat")}
                      </span>
                    </li>
                  ))}
                </ul>
              </Card>
            </>
          )}

          {/* `A4`, D2 52-money: « Comment le règlement se passe », closed, between the sales and the movements. */}
          <details
            className="group rounded-2xl border border-gray-200 bg-white px-5"
            data-testid="solde-comment"
          >
            {/* `P1-DLW-054` (designer R, batch 11, frame 81): a ROW the height of a card header, the kit's chevron (down closed, up open) and a visible focus ring; the words are unchanged. */}
            <summary className="-mx-2 flex min-h-11 cursor-pointer items-center justify-between gap-3 rounded-lg px-2 py-3 text-sm font-semibold marker:content-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-600 [&::-webkit-details-marker]:hidden">
              {t("dealer.solde.commentTitre")}
              <ChevronDown
                data-chevron=""
                aria-hidden="true"
                className="h-5 w-5 shrink-0 text-gray-500 transition-transform group-open:rotate-180"
              />
            </summary>
            <div className="flex flex-col gap-3 pb-4 text-sm leading-relaxed text-gray-700">
              <p className="m-0">{t("dealer.solde.commentUn")}</p>
              <p className="m-0">{t("dealer.solde.commentDeux")}</p>
              <p className="m-0">{t("dealer.solde.commentTrois")}</p>
            </div>
          </details>

          {/* « Mouvements » above its card, as the drawing puts it (`F5`). */}
          <h2 className="text-sm font-semibold">{t("dealer.solde.mouvements")}</h2>
          <Card>
            {ledger.movements.length === 0 ? (
              <p className={`text-sm ${TEXTE_SECONDAIRE}`} data-testid="solde-mouvements-absents">
                {t("dealer.solde.mouvementsAbsents")}
              </p>
            ) : (
              <ul className="divide-y divide-gray-100" data-testid="solde-mouvements">
                {ledger.movements.map((movement) => (
                  <li
                    key={movement.id}
                    className="flex items-center gap-3 py-3 first:pt-0 last:pb-0"
                    data-testid="solde-mouvement"
                  >
                    {movement.amount > 0 ? (
                      <TrendingUp className="h-4 w-4 shrink-0 text-emerald-500" aria-hidden="true" />
                    ) : (
                      <TrendingDown className="h-4 w-4 shrink-0 text-gray-400" aria-hidden="true" />
                    )}
                    <div className="flex-1">
                      <p className="text-sm text-gray-700">{movement.label}</p>
                      <p className={META_SIZE} style={META_STYLE} data-testid="solde-mouvement-meta">
                        {movement.reference} · {formatDate(asDate(movement.movedOn))}
                      </p>
                    </div>
                    <span
                      className={`text-sm font-medium tabular-nums ${movement.amount > 0 ? "text-emerald-700" : "text-gray-700"}`}
                    >
                      {/* The designer's ruling of 2 November: a debit is the TRUE minus U+2212 through the catalogue's `formatMovement`; a credit keeps « + ». */}
                      {movement.amount > 0 ? (
                        <>
                          {"+ "}
                          <Money amount={movement.amount} />
                        </>
                      ) : (
                        formatMovement(movement.amount)
                      )}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </Card>

          {/* `A4`, D2 52-money: the money line, last on the page. The rate is the door's (T-1c's two layers), never a literal. */}
          {rate === null ? null : (
            <p className="text-sm leading-relaxed" style={META_STYLE} data-testid="solde-commission">
              {t("dealer.solde.commissionLigne").replace("{taux}", String(rate))}
            </p>
          )}

          <Modal
            open={ouvert}
            title={t("dealer.solde.recharger")}
            onClose={() => {
              setOuvert(false);
            }}
            closeLabel={t("common.action.annuler")}
            actions={
              <>
                <Button
                  variant="secondary"
                  onClick={() => {
                    setOuvert(false);
                  }}
                >
                  {t("common.action.annuler")}
                </Button>
                <Button
                  variant="primary"
                  onClick={() => {
                    void declarer();
                  }}
                  data-testid="solde-recharge-declarer"
                >
                  {t("dealer.solde.rechargeDeclarer").replace(
                    "{montant}",
                    formatMoney(amountValid ? chosenAmount : 0),
                  )}
                </Button>
              </>
            }
          >
            <div className="flex flex-col gap-4" data-testid="solde-recharge">
              <p className={`text-sm ${TEXTE_SECONDAIRE}`}>{t("dealer.solde.rechargeExplication")}</p>

              {/* Hakili's account, from the settings the founder published. */}
              {ledger.hakiliAccount === null ? (
                <p
                  className="rounded-xl bg-amber-50 px-3 py-2 text-xs text-amber-700"
                  data-testid="solde-recharge-compte-absent"
                >
                  {t("dealer.solde.rechargeCompteAbsent")}
                </p>
              ) : (
                <div
                  className="rounded-xl border border-gray-200 px-3 py-2.5"
                  data-testid="solde-recharge-compte"
                >
                  <p className={`${METADONNEE} ${TEXTE_SECONDAIRE}`}>{t("dealer.solde.rechargeCompte")}</p>
                  <p className="mt-1 text-sm text-gray-900">
                    {[ledger.hakiliAccount.bankName, ledger.hakiliAccount.accountName]
                      .filter((part): part is string => part !== null)
                      .join(" · ")}
                  </p>
                  <p className="text-sm font-medium tabular-nums text-gray-900">
                    {ledger.hakiliAccount.accountNumber}
                  </p>
                </div>
              )}

              <div>
                <p className="mb-2 text-xs font-medium text-gray-700">{t("dealer.solde.rechargeMontant")}</p>
                <div className="flex flex-wrap items-center gap-2">
                  {PRESETS.map((preset) => (
                    <button
                      key={preset}
                      type="button"
                      onClick={() => {
                        setAmount(preset);
                        setCustomAmount("");
                      }}
                      className={`rounded-lg border px-3 py-2 text-xs font-medium ${
                        chosenAmount === preset
                          ? "border-indigo-200 bg-indigo-50 text-indigo-700"
                          : "border-gray-200 bg-white text-gray-600"
                      }`}
                      data-testid="solde-recharge-montant"
                    >
                      {formatMoney(preset)}
                    </button>
                  ))}
                  {manque === null ? null : (
                    <button
                      type="button"
                      onClick={() => {
                        setAmount(manque);
                        setCustomAmount("");
                      }}
                      className={`rounded-lg border px-3 py-2 text-xs font-medium ${
                        chosenAmount === manque
                          ? "border-indigo-200 bg-indigo-50 text-indigo-700"
                          : "border-rose-200 bg-rose-50 text-rose-700"
                      }`}
                      data-testid="solde-recharge-manque"
                    >
                      {t("dealer.solde.rechargeManque").replace("{montant}", formatMoney(manque))}
                    </button>
                  )}
                </div>
              </div>

              <Field label={t("dealer.solde.rechargeAutreMontant")} htmlFor="solde-recharge-autre">
                <input
                  id="solde-recharge-autre"
                  inputMode="numeric"
                  value={customAmount}
                  onChange={(event) => {
                    setCustomAmount(event.target.value.replace(/\D/gu, ""));
                  }}
                  className="w-full rounded-xl border border-gray-200 px-3 py-2 text-sm"
                  data-testid="solde-recharge-autre"
                />
              </Field>

              <Field
                label={t("dealer.solde.rechargeReference")}
                htmlFor="solde-recharge-reference"
                hint={t("dealer.solde.rechargeReferenceAide")}
              >
                <input
                  id="solde-recharge-reference"
                  value={reference}
                  onChange={(event) => {
                    setReference(event.target.value);
                  }}
                  className="w-full rounded-xl border border-gray-200 px-3 py-2 text-sm"
                  data-testid="solde-recharge-reference"
                />
              </Field>

              {erreur === null ? null : (
                <p role="alert" className="text-sm text-rose-600" data-testid="solde-recharge-erreur">
                  {erreur}
                </p>
              )}
            </div>
          </Modal>
        </>
      )}
    </div>
  );
}
